import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

export interface ProfileMenuUser {
  readonly name?: string | null;
  readonly email?: string | null;
  readonly phone?: string | null;
}

/** 아바타 이니셜 한 글자 — 이름 우선, 없으면 이메일·전화번호 순으로 대체한다. */
export function profileMenuInitial(user: ProfileMenuUser | null | undefined): string {
  const src = user?.name ?? user?.email ?? user?.phone ?? '';
  return src.charAt(0).toUpperCase() || '?';
}

/**
 * 헤더 우측 계정 드롭다운(아바타 트리거 + 이름/연락처 + 로그아웃) — 인증 서비스에 의존하지
 * 않는 순수 프레젠테이션 컴포넌트다. `user` input과 `logoutRequested` output만으로 동작해,
 * 인증 방식이 서로 다른 앱(SpinoffAuthService, StaffAuthService, StaffOidcAuthService 등)
 * 모두 각자의 서비스에서 값을 뽑아 넘기기만 하면 재사용할 수 있다(2026-08-12 — 로그인 가능한
 * 모든 앱이 이 컴포넌트를 쓰도록 통일).
 *
 * `[profileMenuSection]` 투영 슬롯 — 계정에 속한 화면 전용 메뉴 항목(예: sbe-commerce의
 * "주문 내역")을 여기 넣는다. 아무것도 투영하지 않으면 렌더링되지 않는다. 투영하는 쪽에서
 * 맨 앞에 `<mat-divider />`를 넣어 구분선을 제어한다 — 이 컴포넌트는 헤더와 투영 슬롯 사이에
 * 자체 구분선을 넣지 않는다(로그아웃 버튼 위 구분선만 소유).
 */
@Component({
  selector: 'sbe-profile-menu',
  imports: [MatIconButton, MatDividerModule, MatIconModule, MatMenuModule],
  template: `
    <button
      mat-icon-button
      type="button"
      [matMenuTriggerFor]="loading() ? null : profileMenu"
      [disabled]="loading()"
      class="pm__trigger"
      [class.pm__trigger--loading]="loading()"
      [attr.aria-busy]="loading() || null"
      [attr.aria-label]="loading() ? '계정 정보 확인 중' : '계정 메뉴 — ' + (user()?.name ?? contactPrimary() ?? '사용자')"
    >
      <span class="pm__avatar">{{ initial() }}</span>
    </button>

    <!--
      xPosition="before" — 이 트리거는 항상 헤더 맨 우측에 있으므로, 메뉴 왼쪽 모서리를
      트리거 왼쪽에 맞춰 오른쪽으로 펼치는 기본값(after)은 화면 우측 밖으로 넘칠 수 있고
      시각적으로도 불안정하다. 메뉴 오른쪽 모서리를 트리거 오른쪽에 맞춰 왼쪽으로 펼친다.
    -->
    <mat-menu #profileMenu="matMenu" xPosition="before">
      <div class="pm__header" [attr.aria-label]="user()?.name ?? contactPrimary() ?? '사용자'">
        <div class="pm__avatar pm__avatar--lg" aria-hidden="true">{{ initial() }}</div>
        <div class="pm__info">
          @if (user()?.name; as name) {
            <span class="pm__name">{{ name }}</span>
          }
          @if (contactPrimary(); as contact) {
            <span class="pm__contact">{{ contact }}</span>
          }
          @if (contactSecondary(); as contact2) {
            <span class="pm__contact">{{ contact2 }}</span>
          }
          @if (!user()?.name && !contactPrimary()) {
            <span class="pm__name">–</span>
          }
        </div>
      </div>

      <ng-content select="[profileMenuSection]" />

      <mat-divider />

      <button mat-menu-item type="button" (click)="logoutRequested.emit()">
        <mat-icon matMenuItemIcon>logout</mat-icon>
        로그아웃
      </button>
    </mat-menu>
  `,
  styles: [
    `
      .pm__trigger {
        width: 36px;
        height: 36px;
        padding: 0;
      }
      /* 로그인 판정이 끝나기 전 상태 — 아바타 크기는 그대로 유지해(레이아웃 시프트 없음)
         반투명 + 펄스로 "아직 모름"을 표시한다. 판정이 끝나면(loading=false) 이 클래스가
         빠지고 곧바로 실제 아바타(이니셜) 또는 로그인 버튼으로 교체된다. */
      .pm__trigger--loading {
        opacity: 0.5;
        animation: pm-pulse 1.5s ease-in-out infinite;
      }
      @keyframes pm-pulse {
        0%,
        100% {
          opacity: 0.35;
        }
        50% {
          opacity: 0.65;
        }
      }
      .pm__avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.2);
        color: inherit;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 0.8125rem;
        text-transform: uppercase;
        flex-shrink: 0;
      }
      .pm__header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        min-width: 200px;
        pointer-events: none;
        user-select: none;
      }
      .pm__avatar--lg {
        width: 36px;
        height: 36px;
        background: var(--mat-sys-primary);
        color: var(--mat-sys-on-primary);
        font-size: 0.875rem;
      }
      .pm__info {
        display: flex;
        flex-direction: column;
        gap: 2px;
        overflow: hidden;
        min-width: 0;
      }
      .pm__name {
        font-size: 0.875rem;
        font-weight: 600;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .pm__contact {
        font-size: 0.75rem;
        opacity: 0.6;
        line-height: 1.4;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileMenuComponent {
  readonly user = input<ProfileMenuUser | null>(null);
  /**
   * 로그인 여부 판정이 아직 끝나지 않은 상태 — true면 트리거를 비활성화하고 반투명 펄스로
   * 표시한다("로그인" 버튼과 실제 프로필 사이에서 잘못된 상태를 잠깐 보여주는 깜빡임 방지).
   * `user()`가 null이면 이니셜은 원래대로 `?`로 폴백되므로 별도 마크업 없이 재사용된다.
   */
  readonly loading = input(false);
  readonly logoutRequested = output<void>();

  protected readonly initial = computed(() => profileMenuInitial(this.user()));

  /** 이메일 우선, 없으면 전화번호 */
  protected readonly contactPrimary = computed(() => this.user()?.email ?? this.user()?.phone ?? null);

  /** 이메일과 전화번호 모두 있을 때 두 번째 줄 */
  protected readonly contactSecondary = computed(() => {
    const u = this.user();
    return u?.email && u?.phone ? u.phone : null;
  });
}
